import { computed, effect, signal } from '@preact/signals'
import { CheckCircle, RefreshCw, XCircle } from 'lucide-preact'
import { api, type ApiOutput } from '../lib/api.ts'
import { DialogModal } from './dialog.tsx'
import { A, navigate, url } from '@01edu/signal-router'
import { formatDate, formatRelativeTimeReactive } from '../lib/date-utils.ts'
import { onEvent } from '../lib/session.ts'
import { entries } from '../../api/entries.ts'
import { eachHour, globalTime } from '../lib/time.ts'
const SYNC_COOLDOWN_MS = 2000 // 2 seconds
const DAY = 24 * 60 * 60

export const syncStatus = api['GET/api/sync/status'].signal()
type SyncProject = ApiOutput['GET/api/sync/status'][number]
type SyncSignal = ReturnType<
  typeof api['POST/api/sync/trigger-project']['signal']
>
const projectSyncDetails = api['GET/api/sync/details'].signal()
const syncTriggersCacheRecord: Record<string, SyncSignal> = {}
const syncTriggersVersion = signal(0)

const isSyncPending = computed(() => {
  syncTriggersVersion.value
  return Object.values(syncTriggersCacheRecord).some((s) => s.pending)
})

const syncError = computed(() => {
  syncTriggersVersion.value
  return Object.values(syncTriggersCacheRecord).find((s) => s.error)
})

export const syncProjectsById = computed(() => {
  const projects = syncStatus.data || []
  return new Map(projects.map((p) => [p.projectId, p]))
})

const refreshSyncDetails = () => {
  const projectId = url.params['sync-project']
  projectId
    ? projectSyncDetails.fetch({ project: projectId })
    : projectSyncDetails.reset()
}

syncStatus.fetch()
onEvent('SYNC_SUCCEEDED', () => {
  syncStatus.fetch()
  refreshSyncDetails()
})
onEvent(['SYNC_STARTED', 'SYNC_FAILED'], () => syncStatus.fetch())

effect(refreshSyncDetails)

const triggerSyncDomain = (projectDomain: string) => {
  const cached = syncTriggersCacheRecord[projectDomain]
  if (cached) return cached.fetch({ projectDomain })
  syncTriggersVersion.value++
  const s = api['POST/api/sync/trigger-project'].signal()
  syncTriggersCacheRecord[projectDomain] = s
  return s.fetch({ projectDomain })
}

const isInCooldown = (projectDomain: string) => {
  const finishedAt = syncTriggersCacheRecord[projectDomain]?.at
  if (!finishedAt) return false
  return globalTime.value.now - finishedAt < SYNC_COOLDOWN_MS
}

const RelativeTime = ({ at }: { at?: number | null }) => {
  const timeText = formatRelativeTimeReactive(at)
  return <>{timeText}</>
}

const getSyncBadgeProps = (project: SyncProject) => {
  if (!project.lastSyncAt) {
    return { Icon: CheckCircle, badgeClass: 'badge-ghost', label: 'Not synced' }
  }

  const isRunning = project.lastSyncType === entries.SYNC_STARTED
  const isFailed = project.lastSyncType === entries.SYNC_FAILED
  const isSuccess = project.lastSyncType === entries.SYNC_SUCCEEDED
  const now = eachHour.value / 1000
  const isRecent = (now - project.lastSyncAt) <= DAY

  if (isRunning) {
    return { Icon: RefreshCw, badgeClass: 'badge-info', label: 'Syncing...' }
  }
  if (isFailed) {
    return { Icon: XCircle, badgeClass: 'badge-error', label: 'Failed' }
  }
  if (isSuccess && isRecent) {
    return { Icon: CheckCircle, badgeClass: 'badge-success', label: 'Synced' }
  }
  if (isSuccess) {
    return { Icon: CheckCircle, badgeClass: 'badge-warning', label: 'Outdated' }
  }
  return { Icon: CheckCircle, badgeClass: 'badge-ghost', label: 'Not synced' }
}

const closeModal = () => {
  for (
    const withError of Object.values(syncTriggersCacheRecord).filter((s) =>
      s.error
    )
  ) {
    withError.reset()
  }
  navigate({
    params: { dialog: null, 'sync-project': null },
    replace: true,
  })
}

const handleSyncClick = () => {
  const projectId = Number(url.params['sync-project'])
  if (!projectId) return

  const project = syncProjectsById.value.get(projectId)
  if (!project) return

  const isRunning = project.lastSyncType === entries.SYNC_STARTED
  if (isInCooldown(project.projectDomain) || isRunning) return

  triggerSyncDomain(project.projectDomain)
}

export const ProjectSyncIndicator = (
  { project }: { project?: SyncProject },
) => {
  if (!project) {
    return <span class='badge badge-ghost badge-sm'>No sync data</span>
  }

  const { Icon, badgeClass, label } = getSyncBadgeProps(project)
  const isRunning = project.lastSyncType === entries.SYNC_STARTED

  return (
    <A
      params={{ dialog: 'sync-details', 'sync-project': project.projectId }}
      class={`badge ${badgeClass} badge-sm gap-1 cursor-pointer
        whitespace-nowrap hover:opacity-80`}
      replace
    >
      <Icon size={12} class={`shrink-0 ${isRunning ? 'animate-spin' : ''}`} />
      <span>{label}</span>
      <span class='opacity-70 text-xs'>
        (<RelativeTime at={project.lastSyncAt} />)
      </span>
    </A>
  )
}

export const ProjectSyncModal = () => {
  const projectId = Number(url.params['sync-project'])
  if (!projectId) return null

  const project = syncProjectsById.value.get(projectId)

  if (!project) return null

  const details = projectSyncDetails.data
  const isRunning = project.lastSyncType === entries.SYNC_STARTED
  const isFailed = project.lastSyncType === entries.SYNC_FAILED
  const isSuccess = project.lastSyncType === entries.SYNC_SUCCEEDED
  const now = Date.now() / 1000
  const isRecent = project.lastSyncAt &&
    (now - project.lastSyncAt) <= DAY
  const isOutdated = isSuccess && !isRecent

  return (
    <DialogModal id='sync-details' onClose={closeModal}>
      <h3 class='text-lg font-bold mb-4'>{project.projectDomain}</h3>

      <div class='space-y-4'>
        {/* Status indicator with sync button */}
        <div class='flex items-center justify-between gap-3 p-4
          bg-base-200 rounded-box'>
          <div class='flex items-center gap-3'>
            {isRunning
              ? (
                <RefreshCw
                  class='text-info flex-shrink-0 animate-spin'
                  size={24}
                />
              )
              : isFailed
              ? <XCircle class='text-error flex-shrink-0' size={24} />
              : isOutdated
              ? <CheckCircle class='text-warning flex-shrink-0' size={24} />
              : <CheckCircle class='text-success flex-shrink-0' size={24} />}
            <div>
              <p class='font-semibold'>
                {isRunning
                  ? 'Synchronization in progress...'
                  : isFailed
                  ? 'Last sync failed'
                  : isOutdated
                  ? 'Last sync outdated'
                  : 'Synced successfully'}
              </p>
              {project.lastSyncAt && (
                <p class='text-xs opacity-70'>
                  Last activity: <RelativeTime at={project.lastSyncAt} />
                </p>
              )}
            </div>
          </div>
          <button
            type='button'
            class='btn btn-sm btn-primary gap-2'
            onClick={handleSyncClick}
            disabled={isRunning || isSyncPending.value}
          >
            <RefreshCw
              size={14}
              class={isRunning || isSyncPending.value ? 'animate-spin' : ''}
            />
            Sync Now
          </button>
        </div>

        {isSuccess && details?.lastSuccessAt && (
          <div class='card bg-success/10 p-4'>
            <div class='flex items-start justify-between mb-2'>
              <h4 class='font-semibold text-success'>
                ✅ Last Successful Sync
              </h4>
              <span class='badge badge-sm badge-success'>
                ID #{details.lastSuccessId}
              </span>
            </div>
            <p class='text-sm mb-3'>
              <strong>Date:</strong> {formatDate(details.lastSuccessAt, 'full')}
              <span class='opacity-70 ml-2'>
                (<RelativeTime at={details.lastSuccessAt} />)
              </span>
            </p>
            <div class='divider my-2'></div>
            <div class='grid grid-cols-2 gap-3 text-sm'>
              <div class='stat bg-success/20 rounded-field p-2 place-items-center'>
                <div class='stat-title text-xs'>Scopes Added</div>
                <div class='stat-value text-lg'>
                  {details.scopesAdded || 0}
                </div>
              </div>
              <div class='stat bg-success/20 rounded-field p-2 place-items-center'>
                <div class='stat-title text-xs'>Participants Added</div>
                <div class='stat-value text-lg'>
                  {details.participantsAdded || 0}
                </div>
              </div>
            </div>
          </div>
        )}

        {isOutdated && (
          <div class='alert alert-warning'>
            <div>
              <div class='font-semibold text-sm mb-1'>⚠️ Sync Outdated</div>
              <span class='text-sm'>
                The last sync was more than 24 hours ago. Consider running a new
                sync to get the latest data.
              </span>
            </div>
          </div>
        )}

        {isFailed && details?.errorMessage && project.lastSyncAt && (
          <div class='card bg-error/10 p-4'>
            <div class='flex items-start justify-between mb-2'>
              <h4 class='font-semibold text-error'>❌ Last Sync Failed</h4>
              <span class='badge badge-sm badge-error'>
                ID #{project.lastSyncId}
              </span>
            </div>
            <p class='text-sm mb-3'>
              <strong>Date:</strong> {formatDate(project.lastSyncAt, 'full')}
              <span class='opacity-70 ml-2'>
                (<RelativeTime at={project.lastSyncAt} />)
              </span>
            </p>
            <div class='divider my-2'></div>
            <div class='alert alert-error'>
              <div>
                <div class='font-semibold text-sm mb-1'>Error Details:</div>
                <span class='text-sm font-mono break-words whitespace-pre-wrap'>
                  {details.errorMessage}
                </span>
              </div>
            </div>
          </div>
        )}

        {!project.lastSyncAt && (
          <div class='alert'>
            <span>No synchronization data available yet.</span>
          </div>
        )}

        {syncError.value && (
          <div class='alert alert-error'>
            <div>
              <div class='font-semibold text-sm mb-1'>Sync Error:</div>
              <span class='text-sm'>{syncError.value}</span>
            </div>
          </div>
        )}
      </div>
    </DialogModal>
  )
}
